const cssVar = (name: string, fallback: string) => `var(${name}, ${fallback})`;

const fontFamily = '"Manrope Variable", "Manrope", ui-sans-serif, system-ui, sans-serif';

export const designTokens = {
  colors: {
    surface: cssVar('--surface', '#F7F8F7'),
    surfaceDim: cssVar('--surface-dim', '#DCE0DD'),
    surfaceBright: cssVar('--surface-bright', '#FBFCFB'),
    surfaceContainerLowest: cssVar('--surface-container-lowest', '#FFFFFF'),
    surfaceContainerLow: cssVar('--surface-container-low', '#F2F4F2'),
    surfaceContainer: cssVar('--surface-container', '#EBEEEC'),
    surfaceContainerHigh: cssVar('--surface-container-high', '#E4E8E5'),
    surfaceContainerHighest: cssVar('--surface-container-highest', '#DDE3DF'),
    onSurface: cssVar('--on-surface', '#1A1D1C'),
    onSurfaceVariant: cssVar('--on-surface-variant', '#46514E'),
    inverseSurface: cssVar('--inverse-surface', '#2E3132'),
    inverseOnSurface: cssVar('--inverse-on-surface', '#F0F1F2'),
    outline: cssVar('--outline', '#737D79'),
    outlineVariant: cssVar('--outline-variant', '#C5CDC9'),
    surfaceTint: cssVar('--surface-tint', '#006972'),
    primary: cssVar('--primary', '#00535B'),
    onPrimary: cssVar('--on-primary', '#FFFFFF'),
    primaryContainer: cssVar('--primary-container', '#006D77'),
    onPrimaryContainer: cssVar('--on-primary-container', '#9BECF7'),
    inversePrimary: cssVar('--inverse-primary', '#82D3DE'),
    secondary: cssVar('--secondary', '#2F6964'),
    onSecondary: cssVar('--on-secondary', '#FFFFFF'),
    secondaryContainer: cssVar('--secondary-container', '#DDEEEA'),
    onSecondaryContainer: cssVar('--on-secondary-container', '#265752'),
    tertiary: cssVar('--tertiary', '#955943'),
    onTertiary: cssVar('--on-tertiary', '#FFFFFF'),
    tertiaryContainer: cssVar('--tertiary-container', '#F3DED5'),
    onTertiaryContainer: cssVar('--on-tertiary-container', '#633A2A'),
    error: cssVar('--error', '#BA1A1A'),
    onError: cssVar('--on-error', '#FFFFFF'),
    errorContainer: cssVar('--error-container', '#FFDAD6'),
    onErrorContainer: cssVar('--on-error-container', '#93000A'),
    primaryFixed: cssVar('--primary-fixed', '#9FF0FB'),
    primaryFixedDim: cssVar('--primary-fixed-dim', '#82D3DE'),
    onPrimaryFixed: cssVar('--on-primary-fixed', '#001F23'),
    onPrimaryFixedVariant: cssVar('--on-primary-fixed-variant', '#004F56'),
    secondaryFixed: cssVar('--secondary-fixed', '#ACEFE7'),
    secondaryFixedDim: cssVar('--secondary-fixed-dim', '#90D3CB'),
    onSecondaryFixed: cssVar('--on-secondary-fixed', '#00201E'),
    onSecondaryFixedVariant: cssVar('--on-secondary-fixed-variant', '#00504B'),
    tertiaryFixed: cssVar('--tertiary-fixed', '#FFDBCE'),
    tertiaryFixedDim: cssVar('--tertiary-fixed-dim', '#FFB59A'),
    onTertiaryFixed: cssVar('--on-tertiary-fixed', '#380D00'),
    onTertiaryFixedVariant: cssVar('--on-tertiary-fixed-variant', '#6F3720'),
    background: cssVar('--background', '#F7F8F7'),
    onBackground: cssVar('--on-background', '#1A1D1C'),
    surfaceVariant: cssVar('--surface-variant', '#DDE3DF'),
    warning: cssVar('--warning', '#805913'),
    attention: cssVar('--attention', '#805913'),
    attentionContainer: cssVar('--attention-container', '#F5E8C8'),
    onAttentionContainer: cssVar('--on-attention-container', '#5F430D'),
    info: cssVar('--info', '#496774'),
    success: cssVar('--success', '#2E6A58'),
    successContainer: cssVar('--success-container', '#DCEDE5'),
    onSuccessContainer: cssVar('--on-success-container', '#245244'),
    memory: cssVar('--memory', '#955943'),
    memoryContainer: cssVar('--memory-container', '#F3DED5'),
    onMemoryContainer: cssVar('--on-memory-container', '#633A2A'),
  },
  typography: {
    displayLg: { fontFamily, fontSize: 48, fontWeight: 800, lineHeight: '56px', letterSpacing: '-0.02em' },
    headlineLg: { fontFamily, fontSize: 32, fontWeight: 700, lineHeight: '40px', letterSpacing: '-0.01em' },
    headlineLgMobile: { fontFamily, fontSize: 28, fontWeight: 700, lineHeight: '36px' },
    headlineMd: { fontFamily, fontSize: 24, fontWeight: 600, lineHeight: '32px' },
    bodyLg: { fontFamily, fontSize: 18, fontWeight: 400, lineHeight: '28px' },
    bodyMd: { fontFamily, fontSize: 16, fontWeight: 400, lineHeight: '24px' },
    labelMd: { fontFamily, fontSize: 14, fontWeight: 600, lineHeight: '20px', letterSpacing: '0.01em' },
    labelSm: { fontFamily, fontSize: 12, fontWeight: 700, lineHeight: '16px', letterSpacing: '0.05em' },
  },
  font: {
    sans: fontFamily,
    mono: '"JetBrains Mono", "IBM Plex Mono", ui-monospace, monospace',
  },
  radius: { sm: '10px', base: '12px', md: '16px', lg: '18px', xl: '24px', full: '9999px' },
  spacing: { base: 8, xs: 4, sm: 12, md: 24, lg: 48, xl: 64, containerMargin: 20, gutter: 16 },
  layout: {
    appPadding: 20,
    statCardMinWidth: 240,
    mapHeight: 260,
    navHeight: 64,
    maxWidth: 1200,
  },
  geo: {
    glassBg: cssVar('--glass-bg', 'rgba(255,255,255,0.70)'),
    glassBorder: cssVar('--glass-border', 'rgba(92,105,100,0.14)'),
    navBg: cssVar('--nav-bg', 'rgba(255,255,255,0.70)'),
  },
  shadows: {
    card: cssVar('--shadow-card', '0 10px 40px rgba(0,83,91,0.035)'),
    focusRing: cssVar('--focus-ring', '0 0 0 2px rgba(0,83,91,0.18)'),
    greenGlow: 'none',
    popover: cssVar('--shadow-popover', '0 12px 30px rgba(25,28,29,0.14)'),
  },
} as const;
